// Admin dashboard — first card surfaces the recent client-error feed
// captured from the rAF try/catch and AppErrorBoundary. Gated server-side
// (`GET /api/_client-error` returns 403 for non-admin), so a non-admin
// who manually navigates to /admin will see an empty feed + a soft 403
// banner — no auth bypass risk.
//
// Surface filter is a simple <select> chip — uses ALL by default so devs
// see the full timeline; switching to "game-loop" or "react-tree" makes
// triage targeted.
import { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import { AlertTriangle, RefreshCw, Users, Infinity as InfinityIcon, Sparkles, Trophy, Bug, Download, Share2, Flag, Trash2, RotateCcw, Smartphone } from "lucide-react";
import { Sparkline } from "../components/Sparkline";
import { toast } from "sonner";

// CSV download helper. Builds a Blob client-side from the trend buckets
// already in memory — no extra round-trip. Quoting kept minimal because
// every column is either a YYYY-MM-DD date or an integer.
function downloadTrendsCsv(buckets) {
  const header = ["date", "new_pilots", "endless_runs", "client_errors"];
  const rows = (buckets || []).map((b) => [
    b.date,
    b.new_pilots ?? 0,
    b.endless_runs ?? 0,
    b.client_errors ?? 0,
  ]);
  const csv = [header, ...rows]
    .map((row) => row.join(","))
    .join("\n") + "\n";
  const today = new Date().toISOString().slice(0, 10);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cosmic-trends-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const SURFACE_FILTERS = [
  { value: "all", label: "All Surfaces" },
  { value: "game-loop", label: "Game Loop (rAF)" },
  { value: "react-tree", label: "React Tree" },
  { value: "unknown", label: "Unknown" },
];

function StatCard({ icon: Icon, label, value, sub, tone, testId }) {
  // Single KPI tile. Tone drives the accent color; sub is optional
  // secondary text (e.g., "best: <name>" beneath the score).
  const accent = tone || "#00ffff";
  return (
    <div
      className="border bg-black/40 p-4 flex items-start gap-3"
      style={{ borderColor: `${accent}40` }}
      data-testid={testId}
    >
      <div
        className="w-9 h-9 flex items-center justify-center border"
        style={{ borderColor: `${accent}80`, color: accent, boxShadow: `0 0 14px ${accent}30` }}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase mb-1"
          style={{ color: `${accent}` }}
        >
          {label}
        </div>
        <div className="font-heading text-2xl tracking-tight tabular-nums truncate" data-testid={`${testId}-value`}>
          {value}
        </div>
        {sub && (
          <div className="text-[11px] text-white/55 font-mono-cosmic truncate mt-0.5">
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

function InstallFunnelCard() {
  // PWA install funnel — collapses the {attributed, completed}
  // aggregate into per-source rows with attempted/installed counts and
  // a derived conversion %. `qr` always renders even with zero data
  // so the admin sees the funnel scaffold; other sources only appear
  // if they have a positive attribution count, so a brand-new DB
  // doesn't render five empty rows.
  //
  // Self-fetching so the time-window tabs can drive their own
  // requests without bouncing through the parent.
  const WINDOWS = [
    { key: "today", label: "Today" },
    { key: "7d", label: "7 Days" },
    { key: "30d", label: "30 Days" },
    { key: "lifetime", label: "Lifetime" },
  ];
  const [window, setWindow] = useState("lifetime");
  const [data, setData] = useState(null);
  const [series, setSeries] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Fire summary + timeseries + hourly in parallel — all three feed
    // the same admin tile so they share a refetch cycle.
    Promise.allSettled([
      api.get(`/telemetry/install-attribution/summary?window=${window}`),
      api.get(`/telemetry/install-attribution/timeseries?window=${window}`),
      api.get(`/telemetry/install-attribution/hourly`),
    ])
      .then(([sumRes, tsRes, hourlyRes]) => {
        if (cancelled) return;
        setData(sumRes.status === "fulfilled" ? sumRes.value.data || null : null);
        setSeries(tsRes.status === "fulfilled" ? tsRes.value.data?.series || {} : {});
        setHourly(hourlyRes.status === "fulfilled" ? hourlyRes.value.data || null : null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [window]);

  const attributed = (data && data.attributed) || {};
  const completed = (data && data.completed) || {};
  const clicked = (data && data.clicked) || {};
  const KNOWN = [
    { src: "qr", label: "QR scan", accent: "#00ffff" },
    { src: "share", label: "Replay share", accent: "#b026ff" },
    { src: "tournament", label: "Bracket invite", accent: "#3aa8ff" },
    { src: "social", label: "Social", accent: "#ff66cc" },
    { src: "direct", label: "Direct", accent: "#e0e7ff" },
    { src: "unknown", label: "Unknown", accent: "#ffd700" },
  ];
  const rows = KNOWN.filter(
    (r) => r.src === "qr" || (attributed[r.src] || 0) > 0
  );
  const totalAttributed = Object.values(attributed).reduce((a, b) => a + (Number(b) || 0), 0);
  const totalCompleted = Object.values(completed).reduce((a, b) => a + (Number(b) || 0), 0);
  const totalConv =
    totalAttributed > 0 ? Math.round((totalCompleted / totalAttributed) * 100) : 0;

  return (
    <section
      className="border bg-black/40 p-4 mb-6"
      style={{ borderColor: "rgba(0,255,255,0.25)" }}
      data-testid="admin-install-funnel"
    >
      <header className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 flex items-center justify-center border flex-shrink-0"
            style={{ borderColor: "rgba(0,255,255,0.6)", color: "#00ffff", boxShadow: "0 0 14px rgba(0,255,255,0.25)" }}
          >
            <Smartphone className="w-4 h-4" />
          </div>
          <div>
            <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#00ffff] mb-1">
              // INSTALL FUNNEL · {window.toUpperCase()}
            </div>
            <div className="font-heading uppercase tracking-tight text-base">
              QR &amp; channel conversion
            </div>
            <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/45 mt-0.5">
              Reads /api/telemetry/install-attribution/summary
            </div>
          </div>
        </div>
        <div className="text-right" data-testid="admin-install-funnel-total">
          <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45">
            {loading ? "Loading…" : "Total Conversion"}
          </div>
          <div className="font-heading text-3xl tracking-tight text-[#ffd700] tabular-nums">
            {totalConv}%
          </div>
          <div className="font-mono-cosmic text-[10px] text-white/45 tabular-nums">
            {totalCompleted.toLocaleString()} / {totalAttributed.toLocaleString()}
          </div>
        </div>
      </header>

      {/* Window tabs — Today / 7d / 30d / Lifetime. Independent of the
          parent admin reload so an analyst can flick between buckets
          without re-fetching every other section. */}
      <div
        className="flex flex-wrap items-center gap-2 mb-4"
        role="tablist"
        aria-label="Install funnel time window"
        data-testid="admin-install-funnel-tabs"
      >
        {WINDOWS.map((w) => {
          const active = w.key === window;
          return (
            <button
              key={w.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setWindow(w.key)}
              className={`px-3 py-1 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition border ${
                active
                  ? "border-[#00ffff] text-[#00ffff] bg-[#00ffff]/10 shadow-[0_0_12px_rgba(0,255,255,0.25)]"
                  : "border-white/15 text-white/55 hover:border-white/35 hover:text-white"
              }`}
              data-testid={`admin-install-funnel-tab-${w.key}`}
            >
              {w.label}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map(({ src, label, accent }) => {
          const att = Number(attributed[src] || 0);
          const inst = Number(completed[src] || 0);
          const clk = Number(clicked[src] || 0);
          const conv = att > 0 ? Math.round((inst / att) * 100) : 0;
          const pct = Math.min(100, conv);
          // Click-to-install dropoff: of pilots who clicked the
          // Download button, how many actually completed the install?
          // We only surface this when clicks ≥ installs — historical
          // installs that predate the click-tracking rollout would
          // otherwise show nonsense ratios like 1200%. The cleaner
          // ratio kicks in automatically once clicks catch up.
          const clickConv =
            clk > 0 && inst <= clk
              ? Math.round((inst / clk) * 100)
              : null;
          return (
            <div
              key={src}
              className="border bg-black/30 p-3"
              style={{ borderColor: `${accent}40` }}
              data-testid={`admin-install-funnel-row-${src}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span
                  className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase"
                  style={{ color: accent }}
                >
                  {label}
                </span>
                <span
                  className="font-heading text-base tabular-nums"
                  style={{ color: accent }}
                  data-testid={`admin-install-funnel-conv-${src}`}
                >
                  {conv}%
                </span>
              </div>
              <div className="h-1 bg-white/8 overflow-hidden mb-2">
                <div
                  className="h-full transition-[width] duration-700"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: accent,
                    boxShadow: `0 0 8px ${accent}aa`,
                  }}
                />
              </div>
              <div className="flex items-baseline justify-between font-mono-cosmic text-[10px] tabular-nums">
                <span className="text-white/45 uppercase tracking-[0.2em]">
                  Visited
                </span>
                <span className="text-white/80">{att.toLocaleString()}</span>
              </div>
              <div className="flex items-baseline justify-between font-mono-cosmic text-[10px] tabular-nums">
                <span className="text-white/45 uppercase tracking-[0.2em]">
                  Installed
                </span>
                <span className="text-[#ffd700]">{inst.toLocaleString()}</span>
              </div>
              <div className="flex items-baseline justify-between font-mono-cosmic text-[10px] tabular-nums">
                <span className="text-white/45 uppercase tracking-[0.2em]">
                  Clicks
                </span>
                <span
                  className="text-white/70"
                  data-testid={`admin-install-funnel-clicks-${src}`}
                >
                  {clk.toLocaleString()}
                  {clickConv !== null && (
                    <span className="ml-1.5 text-white/40 text-[9px]">
                      ({clickConv}% install)
                    </span>
                  )}
                </span>
              </div>
              {/* Day-over-day attribution sparkline. Hidden on the
                  `today` window (1 day = nothing to chart). Falls
                  back to a flat 7-zero line so the layout stays
                  consistent if a brand-new source has no rows yet. */}
              {window !== "today" && (
                <div
                  className="mt-2 pt-2 border-t border-white/8 flex items-center justify-between gap-2"
                  data-testid={`admin-install-funnel-spark-${src}`}
                >
                  <span className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/35">
                    Trend
                  </span>
                  <Sparkline
                    values={(series && series[src]) || []}
                    width={120}
                    height={26}
                    color={accent}
                    ariaLabel={`${label} daily attributions`}
                    testId={`admin-install-funnel-spark-svg-${src}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Hour-of-day heatmap — surfaces only when there's meaningful
          traffic (≥48 attribution events in the last 30 days, the
          floor for hourly bucketing to read as signal rather than
          noise). Renders a compact 24-cell strip with the hottest
          hour highlighted. */}
      {hourly && hourly.total >= 48 && (() => {
        // Timezone-aware view: when `tzLocal !== false`, shift the UTC
        // buckets by the admin's browser offset so peak hours read in
        // their working day. Defaults to local because most admins
        // think in their own timezone, not UTC.
        const offsetMin = new Date().getTimezoneOffset();
        // getTimezoneOffset returns minutes WEST of UTC (UTC-5 ⇒ 300);
        // we want hours EAST of UTC for the rotation (UTC-5 ⇒ -5).
        const offsetHours = -Math.round(offsetMin / 60);
        const tzLabel =
          Intl.DateTimeFormat().resolvedOptions().timeZone || "Local";
        const useLocal = hourly.tzLocal !== false;
        const view = useLocal ? tzLabel : "UTC";
        // `localVisits[h]` = UTC bucket `h - offsetHours mod 24` — i.e.
        // "what was happening at local hour h" maps back to a specific
        // UTC bucket.
        const utcIdx = (h) => ((h - offsetHours) % 24 + 24) % 24;
        const useVisits = useLocal
          ? Array.from({ length: 24 }, (_, h) => hourly.counts[utcIdx(h)] || 0)
          : hourly.counts;
        const useInstalls = useLocal
          ? Array.from({ length: 24 }, (_, h) => (hourly.completed_counts || [])[utcIdx(h)] || 0)
          : (hourly.completed_counts || []);
        const hotHourLocal = useVisits.indexOf(Math.max(...useVisits));
        const hotInstHourLocal =
          useInstalls.length && Math.max(...useInstalls) > 0
            ? useInstalls.indexOf(Math.max(...useInstalls))
            : -1;
        return (
          <div
            className="mt-5 pt-4 border-t border-white/8"
            data-testid="admin-install-funnel-hourly"
          >
            <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#00ffff] flex items-center gap-3">
                // HOUR-OF-DAY · 30D · {view.toUpperCase()}
                <button
                  type="button"
                  onClick={() => setHourly({ ...hourly, tzLocal: !(hourly.tzLocal !== false) })}
                  className="px-2 py-0.5 border border-white/15 hover:border-[#00ffff]/60 text-white/55 hover:text-[#00ffff] transition tabular-nums"
                  data-testid="admin-install-funnel-hourly-tz-toggle"
                  title="Toggle UTC ↔ local timezone view"
                >
                  {hourly.tzLocal === false ? "→ Local" : "→ UTC"}
                </button>
              </div>
              <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/55 flex items-center gap-3">
                <span>
                  Visit peak:{" "}
                  <span className="text-[#ffd700] font-bold tabular-nums">
                    {String(hotHourLocal).padStart(2, "0")}:00
                  </span>
                </span>
                {hotInstHourLocal >= 0 && (
                  <span data-testid="admin-install-funnel-hourly-install-peak">
                    Install peak:{" "}
                    <span className="text-[#ff66cc] font-bold tabular-nums">
                      {String(hotInstHourLocal).padStart(2, "0")}:00
                    </span>
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-end gap-[2px]" style={{ height: 56 }}>
              {useVisits.map((n, h) => {
                const maxVisits = Math.max(...useVisits);
                const px = maxVisits > 0 ? Math.round((n / maxVisits) * 52) : 0;
                const heightPx = n > 0 ? Math.max(px, 4) : 0;
                const isHot = h === hotHourLocal;
                const color = isHot ? "#ffd700" : "#00ffff";
                const completed = Number(useInstalls[h] || 0);
                const completedPx =
                  n > 0 && completed > 0
                    ? Math.min(heightPx, Math.max(2, Math.round((completed / n) * heightPx)))
                    : 0;
                return (
                  <div
                    key={h}
                    className="flex-1 flex flex-col justify-end"
                    data-testid={`admin-install-funnel-hour-${h}`}
                    title={`${String(h).padStart(2, "0")}:00 ${view} · ${n} visits · ${completed} installs`}
                  >
                    <div
                      className="w-full transition-all relative"
                      style={{
                        height: heightPx,
                        background: color,
                        boxShadow: isHot
                          ? `0 0 10px ${color}cc`
                          : heightPx > 0
                          ? `0 0 3px ${color}55`
                          : "none",
                      }}
                    >
                      {completedPx > 0 && (
                        <div
                          className="absolute left-0 right-0 bottom-0"
                          style={{
                            height: completedPx,
                            background: "#ff66cc",
                            boxShadow: "0 0 6px rgba(255,102,204,0.65)",
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-1 font-mono-cosmic text-[8px] tracking-[0.2em] uppercase text-white/35 tabular-nums">
              <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
            </div>
            <div className="flex items-center gap-3 mt-2 font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/45">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 bg-[#00ffff]" /> Visits
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 bg-[#ff66cc]" /> Installs
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 bg-[#ffd700]" /> Peak hour
              </span>
            </div>
          </div>
        );
      })()}
    </section>
  );
}


function TrendCard({ label, values, color, total, testId }) {
  // Trend tile — sparkline + total + last-day delta. The last-day delta
  // (vs. day-before) is the most actionable signal for the admin scrolling
  // the page; bigger numbers (totals) are secondary context.
  const lastVal = values?.[values.length - 1] ?? 0;
  const prevVal = values?.[values.length - 2] ?? 0;
  const delta = lastVal - prevVal;
  const deltaTone = delta > 0 ? "#00ff88" : delta < 0 ? "#ff007f" : "rgba(255,255,255,0.45)";
  const deltaSign = delta > 0 ? "+" : "";
  return (
    <div
      className="border bg-black/40 p-4 flex items-start justify-between gap-3"
      style={{ borderColor: `${color}33` }}
      data-testid={testId}
    >
      <div className="min-w-0">
        <div
          className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase mb-1"
          style={{ color }}
        >
          {label}
        </div>
        <div className="font-heading text-xl tracking-tight tabular-nums">
          {(total ?? 0).toLocaleString()}
        </div>
        <div
          className="font-mono-cosmic text-[10px] tracking-[0.2em] uppercase mt-1"
          style={{ color: deltaTone }}
          data-testid={`${testId}-delta`}
        >
          today {deltaSign}{delta.toLocaleString()} vs y'day
        </div>
      </div>
      <div className="flex-shrink-0">
        <Sparkline
          values={values || []}
          color={color}
          width={140}
          height={42}
          ariaLabel={`${label} 7-day trend`}
          testId={`${testId}-spark`}
        />
      </div>
    </div>
  );
}

/**
 * UserSupportCard — search pilots by email/name/handle and either
 * reset their password or grant them full paid access. Powers the
 * common support tickets: "I forgot my password" and "I paid but
 * webhook failed".
 */
function UserSupportCard() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(null);
  const [revealedPassword, setRevealedPassword] = useState(null);

  const search = async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    try {
      const { data } = await api.get(`/admin/users/search?q=${encodeURIComponent(q)}`);
      setResults(data.results || []);
    } catch { setResults([]); }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}?\nThis is irreversible.`)) return;
    setBusy(`reset:${u.id}`);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setRevealedPassword({ email: data.email, password: data.new_password });
      toast.success("Password reset — share with pilot securely.");
    } catch (e) {
      toast.error("Reset failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(null);
    }
  };

  const grantAccess = async (u) => {
    if (!window.confirm(`Grant full paid access to ${u.email}?`)) return;
    setBusy(`grant:${u.id}`);
    try {
      await api.post(`/admin/users/${u.id}/grant-access`);
      toast.success("Full access granted.");
      search(query);
    } catch (e) {
      toast.error("Grant failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      className="mb-6 border border-[#00ffff]/30 bg-[#050808]/60 p-4"
      data-testid="admin-user-support-card"
    >
      <header className="mb-3">
        <div className="font-heading uppercase tracking-tight text-base flex items-center gap-2">
          <span className="text-[#00ffff]">◈</span>
          Pilot Support
        </div>
        <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/45 mt-0.5">
          Reset locked passwords · grant full access · search by email/name/handle
        </div>
      </header>

      <input
        type="text"
        placeholder="Search email, name, or handle (min 2 chars)…"
        value={query}
        onChange={(e) => { setQuery(e.target.value); search(e.target.value); }}
        className="w-full px-3 py-2 bg-black/50 border border-white/15 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#00ffff]/50 mb-3 font-mono-cosmic"
        data-testid="admin-user-search-input"
      />

      {revealedPassword && (
        <div
          className="mb-3 border border-[#ffd700]/60 bg-[#ffd700]/[0.08] p-3 flex items-center justify-between gap-2 flex-wrap"
          data-testid="admin-revealed-password"
        >
          <div className="min-w-0 flex-1">
            <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-[#ffd700] mb-0.5">
              // New Password For {revealedPassword.email}
            </div>
            <code className="text-sm text-white break-all font-mono">{revealedPassword.password}</code>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(revealedPassword.password);
                toast.success("Copied");
              }}
              className="px-3 py-1.5 border border-white/25 hover:border-white/50 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase"
            >
              Copy
            </button>
            <button
              type="button"
              onClick={() => setRevealedPassword(null)}
              className="px-3 py-1.5 border border-[#ffd700]/50 text-[#ffd700] hover:bg-[#ffd700]/10 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {results.length > 0 && (
        <ul className="border border-white/10 bg-black/30 divide-y divide-white/5">
          {results.map((u) => (
            <li
              key={u.id}
              className="px-3 py-2 flex items-center gap-3 flex-wrap"
              data-testid={`admin-user-row-${u.id.slice(0, 8)}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{u.handle || u.name}</div>
                <div className="font-mono-cosmic text-[10px] tracking-[0.2em] uppercase text-white/55 truncate">
                  {u.email_masked} · access: {u.access}
                  {u.founder_number ? ` · Founder #${u.founder_number}` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => resetPassword(u)}
                disabled={busy === `reset:${u.id}`}
                className="px-3 py-1.5 border border-[#00ffff]/50 text-[#00ffff] hover:bg-[#00ffff]/10 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase disabled:opacity-40"
                data-testid="admin-reset-password-btn"
              >
                {busy === `reset:${u.id}` ? "…" : "Reset PW"}
              </button>
              {u.access !== "paid" && (
                <button
                  type="button"
                  onClick={() => grantAccess(u)}
                  disabled={busy === `grant:${u.id}`}
                  className="px-3 py-1.5 border border-[#ffd700]/50 text-[#ffd700] hover:bg-[#ffd700]/10 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase disabled:opacity-40"
                  data-testid="admin-grant-access-btn"
                >
                  {busy === `grant:${u.id}` ? "…" : "Grant Access"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * StripePaymentsCard — admin-only refund console + Stripe go-live
 * readiness checklist. Pulls from `/api/admin/payments/recent` for the
 * row list and `/api/admin/payments/readiness` for the boolean
 * checklist used to gate Live Mode launch.
 */
function StripePaymentsCard() {
  const [payments, setPayments] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => {
    Promise.allSettled([
      api.get("/admin/payments/recent?limit=10"),
      api.get("/admin/payments/readiness"),
    ]).then(([rec, rd]) => {
      if (rec.status === "fulfilled") setPayments(rec.value.data?.payments || []);
      if (rd.status === "fulfilled") setReadiness(rd.value.data || null);
    });
  };
  useEffect(() => { load(); }, []);

  const refund = async (sid) => {
    if (!window.confirm(`Refund Stripe session ${sid.slice(0, 12)}…?\nThis is irreversible.`)) return;
    setBusy(sid);
    try {
      await api.post("/admin/payments/refund", { session_id: sid, reason: "requested_by_customer" });
      toast.success("Refund issued", { description: "Stripe will return funds in 5–10 business days." });
      load();
    } catch (e) {
      toast.error("Refund failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(null);
    }
  };

  const mode = readiness?.mode || "unknown";
  const isLive = readiness?.is_live;
  const checklist = readiness?.checklist || {};
  const checks = [
    { key: "live_api_key",                label: "Stripe LIVE secret key (sk_live_…)" },
    { key: "publishable_key_set",         label: "Publishable key configured (pk_live_…)" },
    { key: "webhook_secret_set",          label: "Webhook signing secret set" },
    { key: "at_least_one_test_payment",   label: "≥1 successful test transaction" },
    { key: "refund_flow_exercised",       label: "Refund flow exercised once" },
  ];
  const passing = checks.filter((c) => checklist[c.key]).length;

  return (
    <section
      className="mb-6 border border-[#ffd700]/30 bg-[#0a0808]/60 p-4"
      data-testid="admin-stripe-payments-card"
    >
      <header className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="font-heading uppercase tracking-tight text-base flex items-center gap-2">
            <span className="text-[#ffd700]">$</span>
            Stripe Payments
            <span
              className={`px-2 py-0.5 text-[9px] tracking-[0.3em] uppercase border ${
                isLive
                  ? "border-[#39ff14] text-[#39ff14]"
                  : "border-[#ffd700] text-[#ffd700]"
              }`}
              data-testid="admin-stripe-mode-pill"
            >
              {mode === "live" ? "LIVE MODE" : mode === "test" ? "TEST MODE" : "NO KEY"}
            </span>
          </div>
          <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/45 mt-0.5">
            Refund console · go-live readiness · {passing}/{checks.length} checks passing
          </div>
        </div>
      </header>

      {/* Readiness checklist */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
        {checks.map((c) => {
          const ok = !!checklist[c.key];
          return (
            <div
              key={c.key}
              className="flex items-center gap-2 px-3 py-2 border bg-black/40"
              style={{ borderColor: ok ? "#39ff1455" : "#ff007f55" }}
              data-testid={`admin-stripe-check-${c.key}`}
            >
              <span
                className="w-4 h-4 flex items-center justify-center text-[10px] font-mono-cosmic"
                style={{
                  color: ok ? "#39ff14" : "#ff007f",
                  border: `1px solid ${ok ? "#39ff14" : "#ff007f"}`,
                }}
              >
                {ok ? "✓" : "·"}
              </span>
              <span className="text-xs text-white/80">{c.label}</span>
            </div>
          );
        })}
      </div>

      {/* Recent payments + refund */}
      <div className="border border-white/10 bg-black/30">
        <div className="px-3 py-2 border-b border-white/10 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/55">
          Recent paid sessions ({payments.length})
        </div>
        {payments.length === 0 ? (
          <div className="p-4 text-xs text-white/40 font-mono-cosmic">
            No paid transactions yet. Once players buy skins, the rows will appear here with a Refund button.
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {payments.map((p) => (
              <li
                key={p.session_id}
                className="px-3 py-2 flex items-center gap-3 flex-wrap"
                data-testid={`admin-payment-row-${p.session_id?.slice(0, 8)}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono-cosmic text-[10px] uppercase tracking-[0.2em] text-white/55 truncate">
                    {p.session_id}
                  </div>
                  <div className="text-xs text-white/80">
                    {p.skin_id || "—"} · ${p.amount.toFixed(2)} {p.currency.toUpperCase()}
                  </div>
                </div>
                {p.refunded ? (
                  <span
                    className="px-2 py-1 border border-[#ff007f] text-[#ff007f] font-mono-cosmic text-[9px] tracking-[0.3em] uppercase"
                    data-testid="admin-payment-refunded-pill"
                  >
                    Refunded
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => refund(p.session_id)}
                    disabled={busy === p.session_id}
                    className="px-3 py-1.5 border border-[#ff007f]/60 text-[#ff007f] hover:bg-[#ff007f]/10 hover:border-[#ff007f] font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition disabled:opacity-40"
                    data-testid="admin-payment-refund-btn"
                  >
                    {busy === p.session_id ? "Refunding…" : "Refund"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export default function Admin() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [shareStats, setShareStats] = useState(null);
  const [chatReports, setChatReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    setForbidden(false);
    // Fire all reads in parallel — they share the same admin gate so
    // one 403 implies the others will too. Each tolerates the others
    // failing so the panel stays partially functional.
    const [errRes, statsRes, trendsRes, shareRes, modRes] = await Promise.allSettled([
      api.get("/_client-error"),
      api.get("/admin/stats"),
      api.get("/admin/trends?days=7"),
      api.get("/admin/skin-share-stats?days=7"),
      api.get("/tournaments/admin/chat-reports"),
    ]);
    if (errRes.status === "fulfilled") {
      setItems(errRes.value.data?.items || []);
    } else if (errRes.reason?.response?.status === 403) {
      setForbidden(true);
    }
    if (statsRes.status === "fulfilled") {
      setStats(statsRes.value.data || null);
    } else if (statsRes.reason?.response?.status === 403) {
      setForbidden(true);
    }
    if (trendsRes.status === "fulfilled") {
      setTrends(trendsRes.value.data || null);
    } else if (trendsRes.reason?.response?.status === 403) {
      setForbidden(true);
    }
    if (shareRes.status === "fulfilled") {
      setShareStats(shareRes.value.data || null);
    } else if (shareRes.reason?.response?.status === 403) {
      setForbidden(true);
    }
    if (modRes.status === "fulfilled") {
      setChatReports(modRes.value.data?.reports || []);
    } else if (modRes.reason?.response?.status === 403) {
      setForbidden(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => (it.surface || "unknown") === filter);
  }, [items, filter]);

  // Resolve a single error row. Optimistically drops it from local state
  // so the click feels instant — on a 4xx/5xx we restore the row and
  // surface a toast. The "Refresh" button re-fetches from the server
  // either way, so an in-flight failure can't desync for long.
  const handleResolve = async (errorId) => {
    if (!errorId) return;
    const prev = items;
    setItems((cur) => cur.filter((it) => it.id !== errorId));
    try {
      await api.post(`/_client-error/${errorId}/resolve`);
      toast.success("Error marked resolved");
    } catch (e) {
      setItems(prev);
      toast.error("Couldn't resolve — try refresh");
    }
  };

  const counts = useMemo(() => {
    const out = {};
    for (const it of items) {
      const s = it.surface || "unknown";
      out[s] = (out[s] || 0) + 1;
    }
    return out;
  }, [items]);

  return (
    <div className="min-h-screen bg-[#05050a] text-white" data-testid="admin-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 pt-24 pb-16">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="font-mono-cosmic text-[10px] tracking-[0.35em] text-[#b026ff] uppercase mb-1">
              // Admin
            </div>
            <h1 className="font-heading text-3xl tracking-tight uppercase">
              Mission Control
            </h1>
          </div>
          <button
            type="button"
            onClick={load}
            className="flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#00ffff] text-white/80 hover:text-[#00ffff] font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {forbidden && (
          <div
            className="border border-[#ff007f]/40 bg-[#ff007f]/5 p-4 flex items-start gap-3 mb-6"
            data-testid="admin-forbidden"
          >
            <AlertTriangle className="w-4 h-4 text-[#ff007f] mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-heading uppercase text-sm tracking-tight text-[#ff007f] mb-1">
                403 · Admin Only
              </div>
              <div className="text-white/65 text-sm leading-snug">
                You're signed in, but this account isn't the admin. Sign in
                as the admin pilot to view client error reports.
              </div>
            </div>
          </div>
        )}

        {!forbidden && (
          <section
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6"
            data-testid="admin-stats-grid"
          >
            <StatCard
              icon={Users}
              label="Active Pilots · 24h"
              value={(stats?.pilots?.active_24h ?? 0).toLocaleString()}
              sub={`${(stats?.pilots?.total ?? 0).toLocaleString()} total enlisted`}
              tone="#00ffff"
              testId="admin-stat-active"
            />
            <StatCard
              icon={InfinityIcon}
              label="Endless Runs"
              value={(stats?.endless?.total_runs ?? 0).toLocaleString()}
              sub={
                stats?.endless?.top
                  ? `Top: ${stats.endless.top.name} · ${stats.endless.top.score.toLocaleString()} pts · w${stats.endless.top.waves}`
                  : "No runs yet"
              }
              tone="#b026ff"
              testId="admin-stat-endless"
            />
            <StatCard
              icon={Sparkles}
              label="Moonstones in Circulation"
              value={(stats?.moonstones_in_circulation ?? 0).toLocaleString()}
              sub="Total wallet balance across all pilots"
              tone="#ffd700"
              testId="admin-stat-moonstones"
            />
            <StatCard
              icon={Trophy}
              label="Top Campaign Score"
              value={(stats?.top_total_score?.score ?? 0).toLocaleString()}
              sub={stats?.top_total_score ? `Pilot: ${stats.top_total_score.name}` : "No scores yet"}
              tone="#ff007f"
              testId="admin-stat-top-score"
            />
            <StatCard
              icon={Bug}
              label="Client Errors · 24h"
              value={(stats?.client_errors_24h ?? 0).toLocaleString()}
              sub="From rAF + React boundary telemetry"
              tone={(stats?.client_errors_24h ?? 0) > 0 ? "#ff007f" : "#00ff88"}
              testId="admin-stat-errors-24h"
            />
          </section>
        )}

        {/* INSTALL FUNNEL — surfaces the public `/telemetry/install-
            attribution/summary` aggregate. Self-fetching with Today /
            7d / 30d / Lifetime tabs so it can refetch independently
            of the rest of the admin dashboard. */}
        <InstallFunnelCard />

        {/* STRIPE PAYMENTS — refund console + go-live readiness
            checklist. Admin-only; tolerates 403 silently by leaving
            the card empty. */}
        {!forbidden && <StripePaymentsCard />}
        {!forbidden && <UserSupportCard />}

        {!forbidden && (
          <section className="mb-6" data-testid="admin-trends-section">
            <header className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
              <div>
                <div className="font-heading uppercase tracking-tight text-base">
                  7-Day Trend
                </div>
                <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/45 mt-0.5">
                  Daily buckets · UTC days · last point = today (so far)
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {trends?.buckets?.length > 0 && (
                  <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/45">
                    {trends.buckets[0].date} → {trends.buckets[trends.buckets.length - 1].date}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => downloadTrendsCsv(trends?.buckets)}
                  disabled={!trends?.buckets?.length}
                  className="flex items-center gap-2 px-3 py-1.5 border border-[#00ffff]/40 text-[#00ffff] hover:bg-[#00ffff]/10 hover:border-[#00ffff] disabled:opacity-40 disabled:cursor-not-allowed font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition"
                  data-testid="admin-trends-export-csv"
                  title="Download last 7 days as CSV"
                >
                  <Download className="w-3 h-3" />
                  Export CSV
                </button>
              </div>
            </header>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <TrendCard
                label="New Pilots"
                color="#00ffff"
                values={(trends?.buckets || []).map((b) => b.new_pilots || 0)}
                total={(trends?.buckets || []).reduce((a, b) => a + (b.new_pilots || 0), 0)}
                testId="admin-trend-new-pilots"
              />
              <TrendCard
                label="Endless Runs"
                color="#b026ff"
                values={(trends?.buckets || []).map((b) => b.endless_runs || 0)}
                total={(trends?.buckets || []).reduce((a, b) => a + (b.endless_runs || 0), 0)}
                testId="admin-trend-endless-runs"
              />
              <TrendCard
                label="Client Errors"
                color="#ff007f"
                values={(trends?.buckets || []).map((b) => b.client_errors || 0)}
                total={(trends?.buckets || []).reduce((a, b) => a + (b.client_errors || 0), 0)}
                testId="admin-trend-client-errors"
              />
            </div>
          </section>
        )}

        {!forbidden && (
          <section className="mb-6 border border-[#b026ff]/25 bg-[#b026ff]/5 p-4" data-testid="admin-share-stats-section">
            <header className="flex items-baseline justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Share2 className="w-4 h-4 text-[#b026ff]" />
                <div>
                  <div className="font-heading uppercase tracking-tight text-base">
                    Top Viral Skins · 7d
                  </div>
                  <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/45 mt-0.5">
                    Visits from shared social cards · UTM-attributed
                  </div>
                </div>
              </div>
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#b026ff] tabular-nums">
                {(shareStats?.total_visits ?? 0).toLocaleString()} total visits
              </div>
            </header>
            {shareStats?.top_skins?.length ? (
              <div className="space-y-1.5" data-testid="admin-share-stats-list">
                {shareStats.top_skins.map((row, i) => (
                  <div
                    key={row.skin_id}
                    className="flex items-center justify-between gap-3 px-3 py-2 border border-white/10 bg-black/30"
                    data-testid={`admin-share-stat-${row.skin_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/40 tabular-nums w-6">
                        #{i + 1}
                      </span>
                      <span className="font-mono-cosmic text-xs tracking-wider text-white truncate">
                        {row.skin_id}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className="text-right">
                        <div className="font-heading text-sm tracking-tight tabular-nums text-[#b026ff]">
                          {row.visits.toLocaleString()}
                        </div>
                        <div className="font-mono-cosmic text-[8px] tracking-[0.25em] uppercase text-white/40">
                          visits
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-heading text-sm tracking-tight tabular-nums text-white/70">
                          {row.unique_visitors.toLocaleString()}
                        </div>
                        <div className="font-mono-cosmic text-[8px] tracking-[0.25em] uppercase text-white/40">
                          unique
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/35">
                // No share visits yet — pilots haven't started spreading the comet love.
              </div>
            )}
          </section>
        )}

        {!forbidden && (
          <ChatModerationSection
            reports={chatReports}
            onChange={(updater) => setChatReports(updater)}
          />
        )}

        {!forbidden && (
          <section className="border border-white/10 bg-black/40">
            <header className="px-4 py-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-heading uppercase tracking-tight text-base">
                  Recent Client Errors
                </div>
                <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/45 mt-0.5">
                  Last 100 · capped feed · 30s dedup per pilot/surface
                </div>
              </div>
              <div className="flex items-center gap-2 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase">
                {SURFACE_FILTERS.map((f) => {
                  const count = f.value === "all" ? items.length : (counts[f.value] || 0);
                  const active = filter === f.value;
                  return (
                    <button
                      key={f.value}
                      type="button"
                      onClick={() => setFilter(f.value)}
                      className={`px-2 py-1 border transition ${
                        active
                          ? "border-[#00ffff] text-[#00ffff] bg-[#00ffff]/10"
                          : "border-white/15 text-white/55 hover:border-white/40 hover:text-white/80"
                      }`}
                      data-testid={`admin-filter-${f.value}`}
                    >
                      {f.label} <span className="opacity-60 ml-1">{count}</span>
                    </button>
                  );
                })}
              </div>
            </header>

            <div className="divide-y divide-white/5" data-testid="admin-error-list">
              {loading && (
                <div className="px-4 py-8 text-center text-white/45 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase">
                  Loading…
                </div>
              )}
              {!loading && filtered.length === 0 && (
                <div className="px-4 py-8 text-center text-white/45 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase">
                  No errors {filter !== "all" ? `for "${filter}"` : ""} · clean ship
                </div>
              )}
              {filtered.map((it, idx) => (
                <details key={it.id || idx} className="group">
                  <summary
                    className="px-4 py-3 cursor-pointer flex items-start gap-3 hover:bg-white/5 transition"
                    data-testid={`admin-error-row-${idx}`}
                  >
                    <span
                      className={`mt-0.5 px-1.5 py-0.5 font-mono-cosmic text-[8px] tracking-[0.2em] uppercase border ${
                        it.surface === "game-loop"
                          ? "border-[#00ffff]/50 text-[#00ffff]"
                          : it.surface === "react-tree"
                          ? "border-[#ff007f]/50 text-[#ff007f]"
                          : "border-white/30 text-white/55"
                      }`}
                    >
                      {it.surface || "unknown"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white/90 font-mono-cosmic truncate">
                        {it.message}
                      </div>
                      <div className="text-[10px] font-mono-cosmic tracking-widest uppercase text-white/45 mt-0.5 truncate">
                        {it.user_email || it.user_id} · {it.url || "-"} · {it.created_at?.slice(0, 19)?.replace("T", " ")}
                      </div>
                    </div>
                    {it.id && (
                      <button
                        type="button"
                        // The row is wrapped in <summary> which toggles
                        // the <details> open/closed on click — stop the
                        // event so resolving doesn't also expand/collapse.
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleResolve(it.id);
                        }}
                        className="shrink-0 self-center px-2 py-1 border border-[#00ff88]/40 text-[#00ff88]/80 hover:border-[#00ff88] hover:text-[#00ff88] hover:bg-[#00ff88]/10 font-mono-cosmic text-[9px] tracking-[0.25em] uppercase transition"
                        data-testid={`admin-error-resolve-${idx}`}
                        title="Mark this error as resolved"
                      >
                        Resolve
                      </button>
                    )}
                  </summary>
                  <div className="px-4 pb-4 pt-1 bg-black/30">
                    {it.stack ? (
                      <pre className="text-[10px] leading-tight text-white/70 whitespace-pre-wrap overflow-x-auto font-mono-cosmic">
{it.stack}
                      </pre>
                    ) : (
                      <div className="text-[10px] text-white/45 font-mono-cosmic">No stack</div>
                    )}
                    {it.user_agent && (
                      <div className="text-[9px] tracking-[0.2em] uppercase text-white/35 mt-2 font-mono-cosmic truncate">
                        {it.user_agent}
                      </div>
                    )}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}


/**
 * Chat moderation queue — last 50 flagged messages across all
 * brackets. Each row shows bracket id, author handle, message body,
 * report count, status (Hidden/Flagged), and per-row actions:
 *
 *   - Hidden message  → Restore (clears the soft-delete fields)
 *   - Visible message → Delete  (soft-deletes via the chat DELETE
 *                                 endpoint; we already have the tid+id
 *                                 from the report row)
 *
 * Optimistic local update on success — the table reflects the change
 * immediately so admins can rip through the queue without a refresh.
 */
function ChatModerationSection({ reports, onChange }) {
  const [busyId, setBusyId] = useState(null);

  const handleRestore = async (mid) => {
    if (!mid) return;
    setBusyId(mid);
    try {
      await api.post(`/tournaments/admin/chat-reports/${mid}/restore`);
      onChange((prev) =>
        prev.map((r) =>
          r.id === mid
            ? { ...r, deleted: false, restored_at: new Date().toISOString() }
            : r,
        ),
      );
      toast.success("Message restored");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't restore");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (tid, mid) => {
    if (!tid || !mid) return;
    if (!window.confirm("Soft-delete this message? Pilots won't see it.")) return;
    setBusyId(mid);
    try {
      await api.delete(`/tournaments/${tid}/chat/${mid}`);
      onChange((prev) =>
        prev.map((r) =>
          r.id === mid
            ? { ...r, deleted: true, deleted_at: new Date().toISOString() }
            : r,
        ),
      );
      toast.success("Message hidden");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      className="mb-6 border border-[#ffb020]/25 bg-[#ffb020]/5 p-4"
      data-testid="admin-chat-moderation-section"
    >
      <header className="flex items-baseline justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Flag className="w-4 h-4 text-[#ffb020]" />
          <div>
            <div className="font-heading uppercase tracking-tight text-base">
              Chat Moderation Queue
            </div>
            <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/45 mt-0.5">
              Last 50 flagged bracket messages · auto-hide at 3 unique reporters
            </div>
          </div>
        </div>
        <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#ffb020] tabular-nums">
          {reports.length} flagged
        </div>
      </header>
      {reports.length === 0 ? (
        <div className="text-center py-6 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/35">
          // No reports — pilots are behaving
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="admin-chat-moderation-list">
          {reports.map((r) => {
            const reports_n = (r.reported_by || []).length;
            const isHidden = !!r.deleted;
            return (
              <div
                key={r.id}
                className={`flex items-center justify-between gap-3 px-3 py-2 border ${
                  isHidden
                    ? "border-white/10 bg-black/40 opacity-60"
                    : "border-[#ff007f]/30 bg-black/30"
                }`}
                data-testid={`admin-chat-report-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/45 mb-0.5 flex items-center gap-2 flex-wrap">
                    <span>BRKT {r.tournament_id}</span>
                    <span>·</span>
                    <span className="text-white/65">
                      {r.name}{r.tag ? `#${r.tag}` : ""}
                    </span>
                    <span>·</span>
                    <span
                      className={
                        isHidden ? "text-white/45" : "text-[#ff007f]"
                      }
                    >
                      {isHidden ? "HIDDEN" : "FLAGGED"} · {reports_n} report{reports_n === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div
                    className={`text-sm break-words ${
                      isHidden ? "text-white/45 line-through" : "text-white"
                    }`}
                  >
                    {r.message}
                  </div>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  {isHidden ? (
                    <button
                      type="button"
                      onClick={() => handleRestore(r.id)}
                      disabled={busyId === r.id}
                      className="flex items-center gap-1.5 px-2 py-1 border border-[#00ff88]/50 text-[#00ff88] hover:bg-[#00ff88]/10 disabled:opacity-40 transition font-mono-cosmic text-[9px] tracking-[0.25em] uppercase"
                      data-testid={`admin-chat-restore-${r.id}`}
                    >
                      <RotateCcw className="w-3 h-3" />
                      Restore
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleDelete(r.tournament_id, r.id)}
                      disabled={busyId === r.id}
                      className="flex items-center gap-1.5 px-2 py-1 border border-[#ff4d6d]/50 text-[#ff4d6d] hover:bg-[#ff4d6d]/10 disabled:opacity-40 transition font-mono-cosmic text-[9px] tracking-[0.25em] uppercase"
                      data-testid={`admin-chat-delete-${r.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                      Hide
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
